import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import Icon from '@/components/ui/Icon';
import CalendarDebugger from '@/components/debug/CalendarDebugger';
import TestHelpers from '@/pages/TestHelpers';
import EmailTestPage from '@/pages/EmailTestPage';
import { User } from '@/api/entities';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AppLoadingSpinner from '@/components/loading/AppLoadingSpinner';

export default function DebugTools() {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadUser = async () => {
      try {
        const currentUser = await User.me();
        setUser(currentUser);
      } catch (error) {
        console.error('Error loading user:', error);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadUser();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
        <AppLoadingSpinner />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="p-4 text-center">
        <h2 className="text-xl font-bold mb-4">Please log in</h2>
        <p className="text-slate-600 mb-4">Log in to access debug tools.</p>
        <button 
          onClick={() => User.login()}
          className="bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg"
        >
          Log In
        </button>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6">
      {/* Page Header */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold text-slate-900">
            Debug Tools
          </h1>
          <Icon name="bug" size="md" color="primary" />
        </div>
        <p className="text-slate-600">
          Development and debugging tools for troubleshooting issues. These tools are only available in development mode.
        </p>
      </div>

      {/* Calendar Debug Tool */}
      <Card className="border-orange-200 bg-orange-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-orange-800">
            <Icon name="alert-circle" size="md" />
            Calendar Debug Tool
            <Badge variant="outline" className="text-orange-700 border-orange-300">
              Debug
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <CalendarDebugger />
        </CardContent>
      </Card>

      {/* Database Migration Helper Tests */}
      <Card className="border-green-200 bg-green-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-green-800">
            <Icon name="database" size="md" />
            Database Migration Helper Tests
            <Badge variant="outline" className="text-green-700 border-green-300">
              Migration
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-green-700">
              Test the new JSONB helper functions for tracked programs and email connections. 
              These functions replace direct database queries with JSONB operations on the profiles table.
            </p>
            <div className="text-xs text-green-600 bg-green-100 p-3 rounded">
              <strong>What's tested:</strong> Create, read, update, delete operations for both tracked_programs and email_connections JSONB columns
            </div>
            <TestHelpers />
          </div>
        </CardContent>
      </Card>

      {/* Progressive Onboarding Debug Tool */}
      <Card className="border-purple-200 bg-purple-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-brand-800">
            <Icon name="users" size="md" />
            Progressive Onboarding Debug Tool
            <Badge variant="outline" className="text-brand-700 border-purple-300">
              Debug
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-brand-700">
              Test the complete onboarding flow including Gmail integration, step-by-step progression, and user experience.
            </p>
            <div className="flex gap-2">
              <Link to="/onboarding-test">
                <Button className="bg-brand-600 hover:bg-brand-700 text-white">
                  <Icon name="play" size="sm" className="mr-2" />
                  Launch Onboarding Test
                </Button>
              </Link>
            </div>
            <div className="text-xs text-brand-600 bg-purple-100 p-2 rounded">
              <strong>Features:</strong> Gmail OAuth, Email processing, Step progression, Skip functionality, Error handling
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Email Testing Tool */}
      <Card className="border-blue-200 bg-blue-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-blue-800">
            <Icon name="mail" size="md" />
            Email Functionality Testing
            <Badge variant="outline" className="text-blue-700 border-blue-300">
              Test
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-blue-700">
              Test all email functionality including Resend API integration, friend invitations, 
              feedback notifications, and password reset emails.
            </p>
            <div className="bg-white rounded-lg p-4 border border-blue-200">
              <EmailTestPage />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Development Notice */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Icon name="info" size="md" color="info" className="mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Development Tools Notice</p>
              <p>
                These debug tools are only available during development and testing. 
                They will not be accessible in the production version of the application.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
